import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { GridPaginationModel } from '@mui/x-data-grid-premium';
import { useUrlState, codecs } from './useUrlState';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/**
 * `resetKey` (the filter value) is compared by identity, not run through a callback:
 * a callback's identity churns with the router location and would reset the page on
 * the navigation that just set it.
 *
 * Page and size share one param because two setters both resolve against the location
 * as it is at call time — the second would drop the first.
 */
export function useServerPagination(defaultPageSize = 50, resetKey?: unknown) {
  const defaults = useRef({ page: 0, pageSize: defaultPageSize });
  const [model, setModel] = useUrlState('page', defaults.current, codecs.pageModel);
  const { page, pageSize } = model;
  const rowCountRef = useRef(0); // a grid handed 0 mid-load clamps the page back to 0

  const paginationModel = useMemo<GridPaginationModel>(() => ({ page, pageSize }), [page, pageSize]);

  const onPaginationModelChange = useCallback(
    (next: GridPaginationModel) => setModel({
      // Changing the page size changes what page 0 means, so start again from the top.
      page: next.pageSize !== pageSize ? 0 : next.page,
      pageSize: next.pageSize,
    }),
    [pageSize, setModel],
  );

  const resetPage = useCallback(() => setModel((prev) => ({ ...prev, page: 0 })), [setModel]);

  const prevResetKey = useRef(resetKey);
  useEffect(() => {
    if (prevResetKey.current === resetKey) return;
    prevResetKey.current = resetKey;
    resetPage();
  }, [resetKey, resetPage]);

  const gridProps = (total: number | undefined) => {
    if (total !== undefined) rowCountRef.current = total;
    return {
      pagination: true as const,
      paginationMode: 'server' as const,
      paginationModel,
      onPaginationModelChange,
      rowCount: rowCountRef.current,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    };
  };

  return {
    gridProps,
    offset: page * pageSize,
    limit: pageSize,
    resetPage,
  };
}
